import filmPoster from "../assets/case-studies/ditto/product-poster.webp";
import outcomeVideo from "../assets/case-studies/ditto/outcome.mp4";
import outcomePoster from "../assets/case-studies/ditto/outcome-poster.webp";
import failHallucination from "../assets/case-studies/ditto/fail-hallucination.webp";
import failCircle from "../assets/case-studies/ditto/fail-circle.webp";
import failEyes from "../assets/case-studies/ditto/fail-eyes.webp";
import failGuardrails from "../assets/case-studies/ditto/fail-guardrails.webp";

export const dittoCaseStudy = {
  slug: "ditto",
  editorialMedia: true,
  motion: true,
  chrome: "meadow",
  paintTheme: "ditto",
  headerLayout: "stacked",
  /* Product film with sound control (public/films/ditto/product.html) */
  headerComponent: "ditto-film-product",
  headerImage: filmPoster,
  headerNote: "Concept on iPhone Duo",
  headerImageAlt: "Ditto game board: two players race to find the toy that appears on both sides",
  title: ["Designed and shipped an AI party game", "with live image generation in 5 days"],
  titleAccent: "in 5 days",
  metaLayout: "facts",
  meta: {
    type: "Master’s class project",
    role: "Designer and developer",
    duration: "5 days",
    year: "2026",
  },
  highlightsLayout: "columns",
  highlightsTitle: "Highlights",
  /* Highlights removed to match QR Pay (2026-10-26); copy kept below, unused */
  highlights: [],
  _highlightsArchive: [
    {
      num: "01",
      title: "I solved for AI token limits",
      body: "Came up with solutions to keep the gameplay sustainable within the limited student budget.",
    },
    {
      num: "02",
      title: "I architected an AI workflow",
      body: "Iteratively engineered input validation, local caching, and fallback systems to guarantee a reliable player experience.",
    },
    {
      num: "03",
      title: "I showcase rapid execution",
      body: "Designed, built, and tested the game in five days, using every AI failure to improve the system.",
    },
  ],
  sections: [
    {
      type: "content",
      label: "Play",
      title: "Ditto is a two-player party game with AI-generated boards",
      paragraphs: [
        "For a master’s class on AI, I was challenged to build something that benefits from non-determinism. I built Ditto, a two-player party game where AI generates a new board every round.",
        "In five days, I designed the experience, built the AI pipeline, and shipped a working game. The challenge was making unpredictable AI outputs fun without letting them get in the way of gameplay.",
      ],
      figure: {
        component: "ditto-play",
      },
    },
    {
      type: "rich",
      label: "Pipeline",
      title: "Two AI models and some code turn a theme into a board",
      blocks: [
        {
          type: "paragraph",
          text: "Every game starts with a theme entered by a player. I built a pipeline where two AI models and a custom utility work together to create the boards. I split the work so each part does what it’s best at.",
        },
        {
          type: "paragraph",
          text: "The text model decides what to illustrate. The image model creates the visuals. Code turns those visuals into a playable, fair game.",
        },
        {
          type: "steps",
          items: [
            { lead: "Text LLM:", text: "Validates the theme and generates a list of distinct items." },
            { lead: "Image generation model:", text: "Turns those items into a single sprite sheet." },
            { lead: "Custom utility:", text: "Slices the sprite sheet into individual icons." },
            { lead: "Game logic:", text: "Arranges the icons into two boards and ensures exactly one match." },
          ],
        },
        {
          type: "figure",
          figure: {
            component: "ditto-build-film",
          },
        },
      ],
    },
    {
      type: "rich",
      label: "Fairness",
      title: "AI creates the variety, and code keeps the game fair",
      blocks: [
        {
          type: "paragraph",
          text: "The same prompt can produce different images every time. That variety makes Ditto fun, but it also means outputs can be inconsistent or unusable.",
        },
        {
          type: "paragraph",
          text: "A surprising icon is part of the fun, but a board with no match breaks the game. So I had to decide where to let AI improvise and where the system needed rules. AI creates the variety. Deterministic code protects the experience.",
        },
        {
          type: "figure",
          figure: {
            component: "nondeterminism-flow",
          },
        },
        {
          type: "spec-table",
          headers: { problem: "CHALLENGE", solution: "MY SOLUTION" },
          rows: [
            {
              problem: "Inconsistent image outputs",
              solution: [{ text: "Strict visual guidelines", emphasis: true }, { text: " in the system prompt" }],
            },
            {
              problem: "Abstract or difficult themes",
              solution: [{ text: "A fast " }, { text: "text model validates themes", emphasis: true }, { text: " before image generation" }],
            },
            {
              problem: "Repeated generation costs",
              solution: [{ text: "Cache generated sets locally", emphasis: true }],
            },
            {
              problem: "API failures or limits",
              solution: [{ text: "Fall back to " }, { text: "device emojis", emphasis: true }],
            },
            {
              problem: "Unfair or duplicate matches",
              solution: [{ text: "Enforce " }, { text: "exactly one match", emphasis: true }, { text: " through game logic" }],
            },
          ],
        },
      ],
    },
    {
      type: "rich",
      label: "Cost",
      title: "Generating icons one by one cost too much, so I used a sprite sheet",
      blocks: [
        {
          type: "paragraph",
          text: "Generating every icon separately was too slow and expensive on a student budget. I changed the pipeline to generate one sprite sheet per round, then built a utility to slice it into individual icons at runtime.",
        },
        {
          type: "paragraph",
          text: "This reduced image-generation API calls from 15 to 1 per round, making the game faster and cheaper to run. I gave up some control over each icon, which was worth it for a game that has to start quickly.",
        },
        {
          type: "figure",
          figure: {
            component: "ditto-sheet",
          },
        },
      ],
    },
    {
      type: "rich",
      label: "Failures",
      title: "The model got things wrong, and the system had to cope",
      blocks: [
        {
          type: "paragraph",
          text: "The model didn’t always interpret instructions as expected. When I asked it to remove plain circles, it also removed the animals’ eyes. Prompt bleed even fused unrelated concepts, like a burger, fries, and a cap, into one icon.",
        },
        {
          type: "paragraph",
          text: "These failures pushed me to give the model better context, use visual references when words weren’t enough, and start each generation with a clean context. I couldn’t make the model perfectly predictable, so I designed the system to handle imperfect outputs.",
        },
        {
          type: "figure-row",
          figures: [
            {
              src: failHallucination,
              alt: "A cyberpunk game board with one icon circled in red, where a burger, fries, and a cap are fused into a single image",
              caption: "Prompt bleed fused a burger, fries, and a cap into one icon.",
              width: 1000,
              height: 737,
            },
            {
              src: failCircle,
              alt: "A game board with a plain circle icon circled in red",
              caption: "Plain circles kept appearing, so I asked the AI to remove them.",
              width: 1000,
              height: 737,
            },
            {
              src: failEyes,
              alt: "An animal game board after the fix, with a tiger circled in red because its eyes are missing",
              caption: "The fix also removed the animals’ eyes.",
              width: 1000,
              height: 737,
            },
          ],
        },
      ],
    },
    {
      type: "content",
      label: "Themes",
      title: "Different themes showed where the system breaks",
      paragraphs: [
        "Players can type anything, so I tried a range of prompts to explore the generation pipeline’s strengths and limits.",
      ],
      bullets: [
        { lead: "Familiar:", text: "Animals, candies, and dinosaurs produced clear, relevant boards." },
        { lead: "Copyrighted characters and brands:", text: "South Park, Disney, and Labubu helped me explore how the system handled recognizable IP." },
        { lead: "Restricted:", text: "Prompts on sensitive topics, like adult content and weapons, tested how the system handled them." },
        { lead: "Abstract:", text: "Happiness, politics, and glass-like transparency were harder to turn into cohesive visuals." },
      ],
      trailingParagraphs: [
        "The experiments helped me understand where theme validation, redirection, or fallback behavior was needed. For example, an abstract theme like politics could be redirected toward a more concrete visual idea, like a ballot box.",
      ],
      figure: {
        src: failGuardrails,
        alt: "South Park is blocked with an error message, and politics suggests trying ballot box instead",
        caption: "The game blocks some themes and redirects abstract ones.",
        role: "full",
        width: 1600,
        height: 524,
      },
    },
    {
      type: "content",
      label: "Testing",
      title: "I watched 8 people play 11 games, and made four changes",
      paragraphs: [
        "They were aged 6 to 58, and they showed me where the experience wasn’t explaining itself clearly. Each change below came from what I saw.",
      ],
    },
    {
      type: "content",
      title: "Players didn’t understand the rules, so the loading screen teaches them",
      paragraphs: [
        "I added an animated tutorial to the loading screen, to teach the game before the first round. Players were already waiting there, so it was the natural moment.",
      ],
      figure: { component: "ditto-film-tutorial", caption: "Animated tutorial card" },
    },
    {
      type: "content",
      title: "Losing players missed the answer, so both players now see it",
      paragraphs: [
        "I added a reveal animation, so both players see the matching icon before moving on. Otherwise the slower player never learns what they were looking for.",
      ],
      figure: { component: "ditto-film-trust", caption: "Correct answer animation for the other player" },
    },
    {
      type: "content",
      title: "Players spam-tapped, so wrong guesses now cost a heart",
      paragraphs: [
        "Tapping everything was faster than looking, which defeats the game. So I gave players three hearts across 10 rounds, and each tap now matters.",
      ],
      figure: { component: "ditto-film-hearts", caption: "Heart being deducted for wrong answers" },
    },
    {
      type: "content",
      title: "Players preferred suggested themes, so presets are easier to find",
      paragraphs: [
        "Typing a theme from scratch is a lot to ask at the start of a party game. I made the suggestions more prominent, which helped players get into a game faster.",
      ],
    },
    {
      type: "outcome-split",
      label: "Outcome",
      title: "A working game in five days, played by my classmates",
      paragraphs: [
        "Ditto started as a class assignment and became a real game I could bring to friends. AI helped me move from an idea to a working experience in five days. I designed the pipeline, worked through model failures, and iterated on the UX with real players.",
      ],
      figure: {
        mp4: outcomeVideo,
        poster: outcomePoster,
        alt: "Classmates and collaborators gathered around an iPad playing Ditto",
        caption: "My classmates and professor playing Ditto",
        role: "outcome",
      },
    },
    {
      type: "reflection",
      label: "Takeaways",
      title: "What I learned building Ditto",
      takeaways: [
        {
          title: "Understand the models, not just the prompts",
          body: "Working with text and image models taught me how their different capabilities shape the experience I can build.",
        },
        {
          title: "Know where AI belongs in the system",
          body: "Generative models are great for creating variety. Deterministic code is better suited to enforcing rules and reliability.",
        },
        {
          title: "Build, playtest, and learn",
          body: "Making a working game helped me understand both the technology and the UX problems in ways a static prototype never could.",
        },
      ],
    },
  ],
};
